import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, AlertCircle, LogIn } from 'lucide-react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { ThemeToggle } from '@/components/ui/theme-toggle';

export function AdminLoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAdminAuth();
  const { user, isAuthenticated } = useAuth();

  // Automaticky zkus přihlášení pokud je uživatel přihlášen
  useEffect(() => {
    if (isAuthenticated && user?.is_admin) {
      handleAutoLogin();
    }
  }, [isAuthenticated, user]);

  const handleAutoLogin = async () => {
    setIsLoading(true);
    setError('');

    try {
      const success = await login();
      
      if (!success) {
        setError('Nemáte admin oprávnění k přístupu do této sekce.');
      }
    } catch (error) {
      setError('Chyba při ověřování admin oprávnění.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualLogin = async () => {
    if (!isAuthenticated) {
      window.location.href = '/login';
      return;
    }

    await handleAutoLogin();
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Language Selector a Theme Toggle v pravém horním rohu */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LanguageSelector variant="minimal" />
        <ThemeToggle />
      </div>
      
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img
              src="/images/blaze-harmony-logo-2025.svg"
              alt="Blaze Harmony Logo"
              className="h-16 w-auto"
            />
          </div>
          <CardDescription>
            Přístup do Admin rozhraní
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {!isAuthenticated ? (
              <div className="text-center space-y-4">
                <p className="text-muted-foreground">
                  Pro přístup do Admin rozhraní se musíte nejdříve přihlásit.
                </p>
                <Button
                  onClick={() => window.location.href = '/login'}
                  className="w-full"
                >
                  <LogIn className="mr-2 h-4 w-4" />
                  Přihlásit se
                </Button>
              </div>
            ) : user?.is_admin ? (
              <div className="text-center space-y-4">
                <p className="text-muted-foreground">
                  Ověřujeme vaše admin oprávnění...
                </p>
                <Button
                  onClick={handleManualLogin}
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Ověřuji oprávnění...
                    </>
                  ) : (
                    <>
                      <Shield className="mr-2 h-4 w-4" />
                      Vstoupit do Admin rozhraní
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="text-center space-y-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Nemáte admin oprávnění pro přístup do této sekce. Kontaktujte správce systému.
                  </AlertDescription>
                </Alert>
                <Button
                  onClick={() => window.location.href = '/dashboard'}
                  variant="outline"
                  className="w-full"
                >
                  Zpět na Dashboard
                </Button>
              </div>
            )}
          </div>

          <div className="mt-6 text-center text-sm text-gray-600">
            <p>Admin rozhraní používá sdílené přihlášení s formulářovou sekcí.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 